import React, { useEffect, useState } from 'react';
import { Window, WindowActionsBar, WindowActionsEvent } from '@progress/kendo-react-dialogs';
import { Button, DropDownButton, DropDownButtonItemClickEvent, SplitButton, SplitButtonItemClickEvent } from '@progress/kendo-react-buttons';
import { useNavigate, useParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import * as svg from '@progress/kendo-svg-icons';
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { TicketApi } from '../../../../../../redux/api/TicketApi';
import { UserApi } from '../../../../../../redux/api/UserApi';
import { PartialSpinny } from '../../../../../Nav/Spinny';
import { SvgIcon } from '@progress/kendo-react-common';
import KBResults from '../../../../FormControls/KBResuilts';
import { KBApi } from '../../../../../../redux/api/KBApi';
import { KBSearchProps } from '../../../../../../types/KB/KBSearchProps';
import { IKBSearchResult } from '../../../../../../types/KB/IKBSearchResult';
import { Card, CardBody, CardHeader } from '@progress/kendo-react-layout';
import { Checkbox, Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { useReduxDispatch, useReduxSelector } from '../../../../../../redux/hooks';
import { SetAction } from '../../../../../../slices/ActionSlice';
import { setNotes } from '../../../../../../slices/EmailActionSlice';
import { setActionExpanded } from '../../../../../../slices/TicketDetailUISlice';
import { ILinkedKB } from '../../../../../../types/KB/ILinkedKB';
import { unescape } from 'lodash';

const KBModal: React.FC = () => {
    const dispatch = useReduxDispatch();
    const navigate = useNavigate();
    const { ticketId } = useParams();
    const [stage, setStage] = useState(isMobile ? 'FULLSCREEN' : 'DEFAULT');
    const { data: userProfile } = UserApi.useGetUserProfileQuery();
    const { data: ticket, isLoading } = TicketApi.useGetTicketDetailsQuery({ ticketId: parseInt(ticketId) });
    const [useDesc, setUseDesc] = React.useState<boolean>(true);
    const [description, setDescription] = React.useState<string>();
    const [freeText, setFreeText] = React.useState<string>();
    const [filter, setFilter] = useState<CompositeFilterDescriptor>(null)
    const [kbResults, setKBResults] = useState<IKBSearchResult[]>([]);
    const [linkedKbs, setLinkedKbs] = useState<IKBSearchResult[]>([]);
    const [error, setError] = useState<string>();
    const [searchKB, { isLoading: searching }] = KBApi.useSearchKBMutation();
    const newAction = useReduxSelector(state => state.actionDetails.action);
    const newEmail = useReduxSelector(state => state.emailAction.email);
    const { data: ticketKbs } = KBApi.useGetTicketKbsQuery(parseInt(ticketId), { skip: !ticketId });
    const [saveTicketdKbs, { isLoading: linkingKbs }] = KBApi.useUpdateLinkedKbsMutation();
    const from = new URLSearchParams(window.location.search).get('from');
    const { data: tenant } = UserApi.useGetTenantQuery()

    //Load the existing linked Kbs into state
    useEffect(() => {
        if (ticketKbs) {
            setLinkedKbs(ticketKbs.map(kb => ({ ID: kb.KB_Id, Title: kb.Title } as IKBSearchResult)));
        }
    }, [ticketKbs]);

    //Set the ticket details into the formstate
    useEffect(() => {
        if (ticket && ticketKbs) {
            setUseDesc(true);
            setDescription(unescape(ticket.Call_Desc));
            doSearch(ticket.Call_Desc);

        }
    }, [ticket, ticketKbs]);

    const doSearch = (desc?: string) => {
        searchKB({ Description: desc || description, EndUserId: userProfile.Id, Ticket_Id: ticket.ID, FreeText: freeText } as KBSearchProps).unwrap().then((data) => {
            if (typeof data === 'number') {
                setKBResults([]);
                return;
            }
            //Need to move all the linked KBs to the top of the list
            let sortedData = data.map(kb => {
                return { ...kb, linked: ticketKbs.map(l => l.KB_Id).indexOf(kb.ID) > -1 }
            }
            ).sort((a, b) => {
                if (a.linked && !b.linked) return -1;
                if (!a.linked && b.linked) return 1;
                return 0;
            });
            //console.log('sortedData', sortedData, linkedKbs)

            setKBResults(sortedData);
        })
    }


    const onFilter = (event: InputChangeEvent) => {
        console.log('Filter', event);
        //setStaff(filterBy(staffList?.slice(), event.filter));
        setFilter({ filters: [{ value: event.value, field: 'Title', operator: 'contains' }] } as CompositeFilterDescriptor);
    }
    const filteredResults = kbResults ? filterBy(kbResults, filter) : [];

    //navigate back when the winow is closed
    const onStageChange = (e: WindowActionsEvent) => {
        //setVisible(false);
        setStage(e.state)
    }

    const onWindowClose = () => {
        navigate(-1);
    }

    const kbSelected = (kb: IKBSearchResult) => {
        setError('');
        //console.log('kbSelected', kb, 'index', linkedKbs, linkedKbs.map(m => m.KB_Id).indexOf(kb.KB_Id));
        /*Add or remove the selected KB from the linked KBs */
        setLinkedKbs(k => {
            var original = [...k]
            original.splice(k.map(m => m.ID).indexOf(kb.ID), 1)
            return k.map(m => m.ID).indexOf(kb.ID) === -1 ? [...k, kb] : original
        });
    }

    const onSearchClicked = () => {
        doSearch();
    }

    const kbLinks = () => linkedKbs.map(kb => `<a href='${tenant.Tenant_Url}/KBAse/?KB_ID=${kb.ID}'>KB${kb.ID} - ${kb.Title}</a>`).join('<br/>');

    const onItemClick = (event: SplitButtonItemClickEvent) => {
        setError('');

        //Create hyperlinks to the selected KB articles so it can be inserted into the ticket, action or email
        switch (event.item.key) {
            case 'ticket':
                //Warn if KBs have been unlinked
                let unlinkedKBs = ticketKbs.filter(kb => linkedKbs.map(l => l.ID).indexOf(kb.KB_Id) === -1);
                if (unlinkedKBs.length > 0 && !window.confirm(`The following KBs have been unlinked from the ${userProfile.Phrases.CallPhrase}: KB${unlinkedKBs.map(kb => kb.KB_Id).join(', KB')}. Are you sure you want to continue?`)) return;


                //Link to ticket
                saveTicketdKbs({ ticketId: parseInt(ticketId), items: linkedKbs.map(kb => { return { KB_Id: kb.ID } as ILinkedKB }) }).unwrap().then(() => {
                    navigate(-1);
                });
                break;
            case 'newAction':
                if (linkedKbs.length === 0) { setError('Please select at least one article to link'); return; }
                addLinksToAction();
                break;
            case 'newEmail':
                if (linkedKbs.length === 0) { setError('Please select at least one article to link'); return; }
                //Link to new email
                addLinksToEmail();
                break;
            default:
                break;
        }

        //go back
        //navigate(-1);
    }
    const linkToTicket = () => {
        //Warn if KBs have been unlinked
        let unlinkedKBs = ticketKbs.filter(kb => linkedKbs.map(l => l.ID).indexOf(kb.KB_Id) === -1);
        if (unlinkedKBs.length > 0 && !window.confirm(`The following KBs have been unlinked from the ${userProfile.Phrases.CallPhrase}: KB${unlinkedKBs.map(kb => kb.KB_Id).join(', KB')}. Are you sure you want to continue?`)) return;


        //Link to ticket
        saveTicketdKbs({ ticketId: parseInt(ticketId), items: linkedKbs.map(kb => { return { KB_Id: kb.ID } as ILinkedKB }) }).unwrap().then(() => {
            navigate(-1);
        });

    }

    //Link to new action
    const addLinksToAction = () => {
        dispatch(SetAction({ ...newAction, HTML_Notes: (newAction.HTML_Notes || '') + '\n' + kbLinks() }));
        dispatch(setActionExpanded(1));
        navigate(-1);
    }
    const addLinksToEmail = () => {
        dispatch(setNotes((newEmail.HTML_Note || '') + '\n' + kbLinks()));
        dispatch(setActionExpanded(2));
        navigate(-1);
    }

    const options = [
        //{ text: 'Ticket', key: 'ticket', icon: 'track-changes-enable' },
        { text: 'Link KB articles to New Action', key: 'newAction', icon: 'comment' },
        { text: 'Link KB articles to New Email', key: 'newEmail', icon: 'email' },
    ]

    if (!userProfile?.Features.Call_Hierarchy) return <></>;

    return (
        <div>
            <Window modal={true} className='cool-window' minimizeButton={() => null} title={<div className='flex align-items-center'><img src='./images/Nav/lifesaver.png' />&nbsp;Knowledge Base Results</div>} onClose={onWindowClose} initialHeight={Math.min(850, window.innerHeight)} initialWidth={Math.min(1200, window.innerWidth)} stage={stage} onStageChange={onStageChange} >
                <div className='position-relative'>
                    {(isLoading || searching || linkingKbs) && <PartialSpinny />}
                    <Card>
                        <CardHeader>
                            <form onSubmit={(e) => { e.preventDefault(); onSearchClicked(); }}>
                                {/* Error message */}
                                {error && <div className={"k-messagebox k-messagebox-error"}>{error}</div>}
                                <div className="mb-3 ms-1">

                                </div>
                                <div className="row mb-3">
                                    <label className="col-sm-1 col-form-label">Tags</label>
                                    <div className="col-sm-11">
                                        <div className="input-group">
                                            <div className="input-group-text">
                                                <Checkbox className='"form-check-input' label={`Use ${userProfile?.Phrases.CallPhrase} description`} checked={useDesc} onChange={(e) => {
                                                    setUseDesc(e.target.value ? true : false);
                                                    if (e.target.value) {
                                                        setDescription(ticket.Call_Desc);
                                                    } else {
                                                        setDescription('');
                                                    }
                                                }} />
                                            </div>
                                            <Input disabled={useDesc} placeholder={useDesc ? ticket.Call_Desc : 'Search Tags'} className='form-control' onChange={(e) => setDescription(e.target.value.toString())} value={description} />

                                        </div>
                                        <span className='text-muted p-2'>space delimited, using 'OR' logic</span>
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <label className="col-sm-1 col-form-label">Contains</label>
                                    <div className="col-sm-11">
                                        <Input placeholder='Title, Description, Solution or KB#' className='form-control' onChange={(e) => setFreeText(e.target.value.toString())} value={freeText} />
                                    </div></div>
                                <Button onClick={onSearchClicked} svgIcon={svg.searchIcon} type='submit' className='justify-content-end p-2 k-button-primary'>
                                    Search Knowledge Base
                                </Button>
                            </form>
                        </CardHeader>
                        <CardBody>
                            {/* <Input className='mb-3 w-100' placeholder='Filter results' onChange={onFilter} /> */}
                            {filteredResults.length === 0 ? <div className='text-center'>No results found</div> :

                                <KBResults results={filteredResults} kbSelected={kbSelected} linkedKBIds={linkedKbs.map(kb => kb.ID)} />
                            }
                        </CardBody>
                    </Card>
                </div>
                <WindowActionsBar>
                    <h6>{linkedKbs?.length} selected</h6>
                    {from === 'action' ? <Button size='large' className="rev-primary-button" svgIcon={svg.linkIcon} onClick={() => addLinksToAction()} >Add to Action note</Button> :
                        from === 'email' ? <Button size='large' className="rev-primary-button" svgIcon={svg.envelopLinkIcon} onClick={() => addLinksToEmail()} >Add to Email</Button> :
                            <>
                                {/* <DropDownButton className="rev-primary-button" popupSettings={{ popupClass: 'rev-primary-button' }} items={options} svgIcon={svg.linkIcon} text="Link KB articles to..." endIcon={<SvgIcon icon={svg.chevronDownIcon} />} /> */}
                                <SplitButton size='large' onButtonClick={linkToTicket} onItemClick={onItemClick} className="rev-primary-button" popupSettings={{ popupClass: 'rev-primary-button' }} items={options} svgIcon={svg.linkIcon} text={"Link KB articles to " + userProfile.Phrases.CallPhrase} />
                            </>
                    }
                    <Button fillMode='outline' onClick={() => onWindowClose()} svgIcon={svg.cancelIcon} className='justify-content-end p-2'>
                        Cancel
                    </Button>
                </WindowActionsBar>
            </Window>
        </div >
    );
};

export default KBModal;